/**
 * AdminHomeDashboard — grouped, card-based, mobile-first Control-Room home.
 *
 * Presentation only. Renders Quick Access tiles + collapsible grouped section
 * cards (config in navGroups.js). Every tile calls `onOpen(target, commsTab)`
 * which maps onto an EXISTING admin tab id, so all routes/panels/permissions
 * are preserved. Live badges (rides/health/fatigue/compliance/inbox) are
 * surfaced on the relevant tiles. Open/closed group state persists per admin
 * in localStorage. No backend / dispatch / lifecycle changes.
 */
import React, { useState, useCallback } from "react";
import { QUICK_ACCESS, NAV_GROUPS } from "./navGroups";
import { ChevronDown, Radio } from "lucide-react";

const LS_KEY = "cd_admin_nav_collapsed";
const readCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
};

// Resolve a tile's live badge into { text, tone } or null.
const tileBadge = (key, badges) => {
  if (!key || !badges) return null;
  if (key === "rides") return badges.rides > 0 ? { text: badges.rides, tone: "amber" } : null;
  if (key === "health") return badges.health > 0 ? { text: badges.health, tone: "red" } : null;
  if (key === "inbox") return badges.inbox > 0 ? { text: badges.inbox, tone: "green" } : null;
  if (key === "corpApps") return badges.corpApps > 0 ? { text: badges.corpApps, tone: "red" } : null;
  if (key === "safety") return badges.safety?.active > 0
    ? { text: badges.safety.active, tone: badges.safety.emergency > 0 ? "red" : "amber" } : null;
  if (key === "fatigue") return badges.fatigue?.count > 0
    ? { text: badges.fatigue.count, tone: badges.fatigue.tone === "over" ? "red" : "amber" } : null;
  if (key === "compliance") return badges.compliance?.count > 0
    ? { text: badges.compliance.count, tone: badges.compliance.tone === "expired" ? "red" : "amber" } : null;
  return null;
};

const TONE = {
  red:   "bg-[#EF4444] text-white",
  amber: "bg-[#F59E0B] text-black",
  green: "bg-[#166534] text-white",
};

const Tile = ({ item, accent, onOpen, badges, large }) => {
  const Icon = item.icon;
  const badge = tileBadge(item.badge, badges);
  return (
    <button
      onClick={() => onOpen(item.target, item.commsTab)}
      className={`group relative flex flex-col items-center justify-center gap-2 rounded-xl border border-border bg-white text-center transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_-8px_rgba(10,10,10,0.25)] active:translate-y-0 ${large ? "py-5 px-2" : "py-4 px-2"}`}
      data-testid={`nav-tile-${item.target}${item.commsTab ? "-" + item.commsTab : ""}`}
      style={{ outlineColor: accent }}
    >
      <span
        className="flex items-center justify-center rounded-lg transition-colors"
        style={{ width: large ? 44 : 38, height: large ? 44 : 38, background: `${accent}14` }}
      >
        <Icon className="w-5 h-5" strokeWidth={2.4} style={{ color: accent }} />
      </span>
      <span className="text-[12px] font-semibold text-foreground leading-tight">{item.label}</span>
      {badge && (
        <span
          className={`absolute top-1.5 right-1.5 min-w-[18px] px-1 py-0.5 rounded-full text-[10px] font-bold text-center ${TONE[badge.tone]} ${badge.tone === "red" ? "animate-pulse" : ""}`}
          data-testid={`nav-badge-${item.target}`}
        >
          {badge.text}
        </span>
      )}
    </button>
  );
};

const GroupCard = ({ group, onOpen, badges, collapsed, onToggle }) => (
  <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`nav-group-${group.key}`}>
    <button
      onClick={() => onToggle(group.key)}
      className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-secondary/30 transition-colors"
      data-testid={`nav-group-toggle-${group.key}`}
    >
      <span className="flex items-center gap-2.5">
        <span className="block w-1 h-4 rounded-full" style={{ background: group.accent }} />
        <span className="label-mini text-foreground">{group.title}</span>
      </span>
      <ChevronDown
        className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
      />
    </button>
    {!collapsed && (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 px-4 pb-4">
        {group.items.map((item) => (
          <Tile key={`${item.target}-${item.commsTab || ""}`} item={item} accent={group.accent} onOpen={onOpen} badges={badges} />
        ))}
      </div>
    )}
  </section>
);

export const AdminHomeDashboard = ({ stats, badges, onOpen }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = useCallback((key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-6" data-testid="admin-home">
      {/* Quick Access */}
      <section data-testid="quick-access">
        <h2 className="label-mini text-muted-foreground mb-3">QUICK ACCESS</h2>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
          {QUICK_ACCESS.map((item) => (
            <Tile key={item.target} item={item} accent={item.accent} onOpen={onOpen} badges={badges} large />
          ))}
        </div>
      </section>

      {/* Grouped sections — single column on mobile, two columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {NAV_GROUPS.map((group) => (
          <GroupCard
            key={group.key}
            group={group}
            onOpen={onOpen}
            badges={badges}
            collapsed={!!collapsed[group.key]}
            onToggle={toggle}
          />
        ))}
      </div>

      {/* Live fleet status */}
      {stats && (
        <div className="rounded-2xl border border-border bg-card px-4 py-3" data-testid="fleet-status-card">
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-3.5 h-3.5 text-[#00C853]" />
            <span className="label-mini text-muted-foreground">FLEET STATUS</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-bold text-[#00C853]">{stats.online_drivers} online</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{Math.max(0, (stats.total_drivers || 0) - (stats.online_drivers || 0))} offline</span>
            <span className="text-muted-foreground">·</span>
            <span className="font-bold">{stats.active_rides} active rides</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomeDashboard;
